/**
 * WidgetFactory - Handles creation of different types of Algolia InstantSearch widgets
 * Extracted from algolia-collection.js for better modularity
 */
class WidgetFactory {
  constructor(algoliaCollection) {
    this.algoliaCollection = algoliaCollection;
    this.instantsearch = window.instantsearch;
  }

  /**
   * Create widgets for all enabled facets
   * @param {Array} enabledFacets - Array of facet configurations
   * @returns {Array} Array of InstantSearch widgets
   */
  createFacetWidgets(enabledFacets) {
    const widgets = enabledFacets.map(facet => {
      const attribute = facet.attribute || facet.name;
      const facetType = facet.type || 'refinementList';
      const containerId = `facet-${attribute.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;

      // Base parameters for all widget types
      const baseParams = {
        container: `#${containerId}`,
        attribute: attribute,
        templates: { header: '' }
      };

      // Create widget based on configured type
      switch (facetType.toLowerCase()) {
        case 'refinementlist':
        case 'refinement_list':
        case 'checkbox':
        case 'disjunctive':
        case 'conjunctive':
          return this.createRefinementListWidget(facet, baseParams);

        case 'rangeslider':
        case 'range_slider':
        case 'range':
        case 'slider':
          return this.createRangeSliderWidget(facet, baseParams);

        case 'menu':
        case 'select':
          return this.createMenuWidget(facet, baseParams);

        case 'toggle':
        case 'togglerefinement':
        case 'toggle_refinement':
          return this.createToggleRefinementWidget(facet, baseParams);

        case 'hierarchicalmenu':
        case 'hierarchical_menu':
        case 'hierarchical':
          return this.createHierarchicalMenuWidget(facet, baseParams);

        default:
          return this.createRefinementListWidget(facet, baseParams);
      }
    }).filter(widget => widget !== null); // Filter out any null widgets

    return widgets;
  }

  /**
   * Create a refinement list widget
   * @param {Object} facet - Facet configuration
   * @param {Object} baseParams - Base widget parameters
   * @returns {Object} InstantSearch refinement list widget
   */
  createRefinementListWidget(facet, baseParams) {
    const attribute = facet.attribute || facet.name;

    const params = {
      ...baseParams,
      limit: facet.limit || this.algoliaCollection.numberOfFiltersVisible || 5,
      showMore: facet.showMore !== false, // Default to true unless explicitly false
      showMoreLimit: facet.showMoreLimit || this.algoliaCollection.maxNumberOfFilters || 20,
      sortBy: facet.sortBy || ['isRefined', 'count:desc', 'name:asc'],
      searchable: false,
      transformItems: (items) => {
        // Smart show more button: only show if there are more items than the current limit
        const limit = params.limit;
        const hasMoreToShow = items.length > limit;

            // Find the widget container and update show more button visibility
            setTimeout(() => {
              this.algoliaCollection.facetManager.updateShowMoreButtonVisibility(params.container, hasMoreToShow, 'RefinementList');
              // Hide facet if it has no items
              this.algoliaCollection.facetManager.hideEmptyFacet(params.container);
            }, 50);

        return items;
      }
    };

    // Check if this is a color facet and apply appropriate styling
    const isColorFacet = this.algoliaCollection.facetManager.isColorFacetByAttribute(attribute) || facet.isColor === true;
    if (isColorFacet) {
      this.algoliaCollection.facetManager.applyColorSwatchStylingShared(params);
    } else {
      this.algoliaCollection.facetManager.applyRegularCheckboxStyling(params);
    }

    return this.instantsearch.widgets.refinementList(params);
  }

  /**
   * Create a range slider widget
   * @param {Object} facet - Facet configuration
   * @param {Object} baseParams - Base widget parameters
   * @returns {Object} InstantSearch range slider widget
   */
  createRangeSliderWidget(facet, baseParams) {
    const params = {
      ...baseParams,
      min: facet.min,
      max: facet.max,
      step: facet.step || 1,
      precision: facet.precision || 0,
      tooltips: {
        format: (value) => {
          if ((facet.format && facet.format === 'currency') || (facet.attribute && facet.attribute.includes('price_'))) {
            return `€${value}`;
          }
          return value.toString();
        }
      }
    };

    // Create custom widget with render function to handle empty state
    const customRangeSlider = this.instantsearch.connectors.connectRangeSlider((renderOptions, isFirstRender) => {
      const { range, start, refine, widgetParams } = renderOptions;
      const container = document.querySelector(widgetParams.container);

      if (!container) return;

      // Check if the range slider should be shown (has valid range)
      const hasValidRange = range?.min !== undefined && range?.max !== undefined && range.min !== range.max;

      if (hasValidRange) {
        // Use the default range slider rendering
        const defaultWidget = this.instantsearch.widgets.rangeSlider(params);
        defaultWidget.render(renderOptions, isFirstRender);
      }

      // Show/hide the facet wrapper
      const wrapper = container.closest('.filter-option');
      if (wrapper) {
        wrapper.style.display = hasValidRange ? '' : 'none';
        wrapper.classList.toggle('facet-empty', !hasValidRange);
      }
    });

    return customRangeSlider(params);
  }

  /**
   * Create a menu widget
   * @param {Object} facet - Facet configuration
   * @param {Object} baseParams - Base widget parameters
   * @returns {Object} InstantSearch menu widget
   */
  createMenuWidget(facet, baseParams) {
    const attribute = facet.attribute || facet.name;

    const params = {
      ...baseParams,
      limit: facet.limit || this.algoliaCollection.numberOfFiltersVisible || 5,
      showMore: facet.showMore !== false,
      showMoreLimit: facet.showMoreLimit || this.algoliaCollection.maxNumberOfFilters || 20,
      sortBy: facet.sortBy || ['isRefined', 'count:desc', 'name:asc'],
      transformItems: (items) => {
        // Smart show more button: only show if there are more items than the current limit
        const limit = params.limit;
        const hasMoreToShow = items.length > limit;

            // Find the widget container and update show more button visibility
            setTimeout(() => {
              this.algoliaCollection.facetManager.updateShowMoreButtonVisibility(params.container, hasMoreToShow, 'Menu');
              // Hide facet if it has no items
              this.algoliaCollection.facetManager.hideEmptyFacet(params.container);
            }, 50);

        return items;
      }
    };

    this.algoliaCollection.facetManager.applyRegularCheckboxStyling(params);
    return this.instantsearch.widgets.menu(params);
  }

  /**
   * Create a toggle refinement widget
   * @param {Object} facet - Facet configuration
   * @param {Object} baseParams - Base widget parameters
   * @returns {Object} InstantSearch toggle refinement widget
   */
  createToggleRefinementWidget(facet, baseParams) {
    const params = {
      ...baseParams,
      on: facet.on || facet.value || true,
      off: facet.off,
      templates: {
        ...baseParams.templates,
        item: `
          <label class="ais-ToggleRefinement-label">
            <input type="checkbox" class="ais-ToggleRefinement-checkbox" {{#isRefined}}checked{{/isRefined}}>
            <span class="ais-ToggleRefinement-labelText">{{label}}</span>
          </label>
        `
      }
    };

    // Create custom widget with render function to handle empty state
    const customToggleRefinement = this.instantsearch.connectors.connectToggleRefinement((renderOptions, isFirstRender) => {
      const { value, refine, widgetParams } = renderOptions;
      const container = document.querySelector(widgetParams.container);

      if (!container) return;

      // Check if the toggle should be shown (has value)
      const hasValue = value?.count > 0;

      if (hasValue) {
        container.innerHTML = `
          <label class="ais-ToggleRefinement-label">
            <input type="checkbox" class="ais-ToggleRefinement-checkbox" ${value.isRefined ? 'checked' : ''}>
            <span class="ais-ToggleRefinement-labelText">${value.label}</span>
          </label>
        `;

        // Add event listener
        container.querySelector('.ais-ToggleRefinement-checkbox')?.addEventListener('change', () => refine(value));
      }

      // Show/hide the facet wrapper
      const wrapper = container.closest('.filter-option');
      if (wrapper) {
        wrapper.style.display = hasValue ? '' : 'none';
        wrapper.classList.toggle('facet-empty', !hasValue);
      }
    });

    return customToggleRefinement(params);
  }

  /**
   * Create a hierarchical menu widget
   * @param {Object} facet - Facet configuration
   * @param {Object} baseParams - Base widget parameters
   * @returns {Object} InstantSearch hierarchical menu widget
   */
  createHierarchicalMenuWidget(facet, baseParams) {
    const attribute = facet.attribute || facet.name;

    const params = {
      ...baseParams,
      attributes: facet.attributes || [facet.attribute || facet.name],
      limit: facet.limit || this.algoliaCollection.numberOfFiltersVisible || 5,
      refined_first: true,
      showMore: facet.showMore !== false,
      showMoreLimit: facet.showMoreLimit || this.algoliaCollection.maxNumberOfFilters || 20,
      separator: facet.separator || ' > ',
      rootPath: facet.rootPath || null,
      showParentLevel: facet.showParentLevel !== false,
      transformItems: (items) => {
        // Smart show more button: only show if there are more items than the current limit
        const limit = params.limit;
        const hasMoreToShow = items.length > limit;

            // Find the widget container and update show more button visibility
            setTimeout(() => {
              this.algoliaCollection.facetManager.updateShowMoreButtonVisibility(params.container, hasMoreToShow, 'HierarchicalMenu');
              // Hide facet if it has no items
              this.algoliaCollection.facetManager.hideEmptyFacet(params.container);
            }, 50);

        return items;
      }
    };

    this.algoliaCollection.facetManager.applyRegularCheckboxStyling(params);
    return this.instantsearch.widgets.hierarchicalMenu(params);
  }

  /**
   * Create a simple category filter widget
   * @returns {Object} InstantSearch refinement list widget for categories
   */
  createSimpleCategoryFilter() {
    const simpleParams = {
      container: '#simple-category-filter',
      attribute: this.algoliaCollection.categoryFilter || 'named_tags.key-container',
      limit: this.algoliaCollection.numberOfFiltersVisible || 5,
      refined_first: true,
      showMore: true,
      showMoreLimit: this.algoliaCollection.maxNumberOfFilters || 20,
      templates: { header: '' },
      transformItems: (items) => {
        // Smart show more button: only show if there are more items than the current limit
        const limit = simpleParams.limit;
        const hasMoreToShow = items.length > limit;

              // Find the widget container and update show more button visibility
              setTimeout(() => {
                this.algoliaCollection.facetManager.updateShowMoreButtonVisibility(simpleParams.container, hasMoreToShow, 'RefinementList');
                // Hide facet if it has no items
                this.algoliaCollection.facetManager.hideEmptyFacet(simpleParams.container);
              }, 50);

        return items;
      }
    };

    // Apply regular checkbox styling
    this.algoliaCollection.facetManager.applyRegularCheckboxStyling(simpleParams);

    return this.instantsearch.widgets.refinementList(simpleParams);
  }

  /**
   * Create a category filter slider widget
   * @returns {Object} InstantSearch refinement list widget for category slider
   */
  createCategoryFilterSlider() {
    const categoryFilterAttribute = this.algoliaCollection.categoryFilter || 'named_tags.key-container';

    return this.instantsearch.widgets.refinementList({
      container: '#category-filter-facet',
      attribute: categoryFilterAttribute,
      limit: 50,
      showMore: false,
      cssClasses: {
        root: `ais-facet-${categoryFilterAttribute.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`,
        list: 'ais-RefinementList-list category-options__list list-unstyled',
        item: 'ais-RefinementList-item slider__slide category-options__item category-options__option',
        label: 'ais-RefinementList-label category-options__option button button--secondary',
        checkbox: 'ais-RefinementList-checkbox visually-hidden',
        selectedItem: 'category-options__option--active'
      },
      templates: {
        header: '',
        item: `

            <label class="ais-RefinementList-label category-options__option button button--secondary" for="{{value}}">
              {{#isRefined}}
              <svg class="icon icon-close icon--sm " width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <title>Close</title> <path fill-rule="evenodd" clip-rule="evenodd" d="M6.34338 6.34315C6.7339 5.95262 7.36707 5.95262 7.75759 6.34315L12.0002 10.5858L16.2429 6.34315C16.6334 5.95262 17.2666 5.95262 17.6571 6.34315C18.0476 6.73367 18.0476 7.36683 17.6571 7.75736L13.4144 12L17.6571 16.2426C18.0476 16.6332 18.0476 17.2663 17.6571 17.6569C17.2666 18.0474 16.6334 18.0474 16.2429 17.6569L12.0002 13.4142L7.75759 17.6569C7.36707 18.0474 6.7339 18.0474 6.34338 17.6569C5.95285 17.2663 5.95285 16.6332 6.34338 16.2426L10.586 12L6.34338 7.75736C5.95285 7.36684 5.95285 6.73367 6.34338 6.34315Z" fill="currentColor"></path> </svg>
              {{/isRefined}}
              {{label}}
            </label>
            <input type="checkbox" id="{{value}}" name="{{value}}" class="ais-RefinementList-checkbox visually-hidden" value="{{value}}" {{#isRefined}}checked{{/isRefined}}>

        `
      }
    });
  }
}

// Make WidgetFactory available globally
window.WidgetFactory = WidgetFactory;
